/**
 * AdminAICreativeDirector.jsx
 *
 * AI Creative Director — Admin Portal studio for the direction engine at
 * src/aiCreativeDirector/. Takes a creative brief, produces pure creative
 * decisions (composition, hierarchy, movement, focus, negative space,
 * balance, trends, storytelling, layout reasoning), and shows the factory
 * plan its adapter derives — with a live "Manufacture" run through the
 * existing, unmodified Template Factory as proof of integration.
 *
 * No network calls, no rendering, no OpenAI yet.
 */
import React, { useMemo, useState } from 'react';
import AdminLayout from './AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Clapperboard, Play, Copy, CheckCircle2, XCircle, Eye, Waves,
  ListOrdered, Crosshair, LayoutTemplate, Sparkles, BookOpen, Factory,
} from 'lucide-react';
import {
  direct,
  toFactoryPlan,
  listAudiences,
  listMoods,
  listBrandStyles,
} from '../../aiCreativeDirector';
import { manufacture } from '../../aiTemplateFactory';

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-medium text-gray-600 mb-1 block">{label}</span>
    {children}
  </label>
);

const SelectBox = ({ value, onChange, options }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className="w-full h-9 rounded-md border border-gray-200 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
  >
    {options.map((o) => (
      <option key={o.value} value={o.value}>{o.label}</option>
    ))}
  </select>
);

const Section = ({ icon: Icon, title, children }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-5">
    <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-3">
      <Icon className="w-4 h-4 text-purple-500" /> {title}
    </h2>
    {children}
  </div>
);

const Chip = ({ children, tone = 'purple' }) => (
  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] ${
    tone === 'purple' ? 'bg-purple-50 text-purple-700'
      : tone === 'gray' ? 'bg-gray-100 text-gray-600'
      : 'bg-amber-50 text-amber-700'
  }`}>
    {children}
  </span>
);

const AdminAICreativeDirector = () => {
  const [audience, setAudience] = useState('restaurant');
  const [mood, setMood] = useState('');
  const [brandStyle, setBrandStyle] = useState('');
  const [seed, setSeed] = useState('');

  const [direction, setDirection] = useState(null);
  const [plan, setPlan] = useState(null);
  const [batch, setBatch] = useState(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const audiences = useMemo(listAudiences, []);
  const moods = useMemo(listMoods, []);
  const styles = useMemo(listBrandStyles, []);

  const runDirector = () => {
    setError('');
    setBatch(null);
    setCopied(false);
    try {
      const dir = direct({
        audience,
        mood: mood || undefined,
        brandStyle: brandStyle || undefined,
        seed: seed.trim() ? Number(seed) : Date.now(),
      });
      setDirection(dir);
      setPlan(toFactoryPlan(dir));
    } catch (e) {
      setDirection(null);
      setPlan(null);
      setError(e.message);
    }
  };

  const runFactory = () => {
    if (!plan) return;
    try {
      setBatch(manufacture({ ...plan, count: 3 }));
    } catch (e) {
      setError(e.message);
    }
  };

  const copyDirection = async () => {
    if (!direction) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(direction, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard unavailable — ignore */ }
  };

  return (
    <AdminLayout>
      <div className="p-6 max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Clapperboard className="w-6 h-6 text-purple-600" /> AI Creative Director
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Turns a brief into premium creative direction — decisions only, no rendering.
              Its adapter feeds the Template Factory unchanged.
            </p>
          </div>
          <Button onClick={runDirector} className="bg-purple-600 hover:bg-purple-700">
            <Play className="w-4 h-4 mr-2" /> Direct
          </Button>
        </div>

        {/* Brief */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Field label="Audience">
              <SelectBox value={audience} onChange={setAudience}
                options={audiences.map((a) => ({ value: a.id, label: a.name }))} />
            </Field>
            <Field label="Mood (optional pin)">
              <SelectBox value={mood} onChange={setMood}
                options={[{ value: '', label: 'Director’s choice' },
                  ...moods.map((m) => ({ value: m.id, label: m.name }))]} />
            </Field>
            <Field label="Brand style (optional pin)">
              <SelectBox value={brandStyle} onChange={setBrandStyle}
                options={[{ value: '', label: 'Director’s choice' },
                  ...styles.map((s) => ({ value: s.id, label: s.name }))]} />
            </Field>
            <Field label="Seed (blank = random)">
              <Input value={seed} placeholder="e.g. 42"
                onChange={(e) => setSeed(e.target.value)} className="h-9 text-sm" />
            </Field>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-2 text-sm text-red-700 mb-6">
            <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" /> {error}
          </div>
        )}

        {!direction && !error && (
          <div className="bg-white rounded-xl border border-dashed border-gray-300 p-16 text-center text-gray-400">
            <Clapperboard className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p className="text-sm">Set a brief and hit <b>Direct</b>.</p>
            <p className="text-xs mt-1">Pins are validated for coherence — a medical flyer refuses a playful mood.</p>
          </div>
        )}

        {direction && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-6">
              <Section icon={Sparkles} title="Creative DNA">
                <div className="flex flex-wrap gap-1.5">
                  <Chip>audience: <b className="ml-1">{direction.audience.name}</b></Chip>
                  <Chip>mood: <b className="ml-1">{direction.mood.name}</b></Chip>
                  <Chip>style: <b className="ml-1">{direction.brandStyle.name}</b></Chip>
                  {direction.trend.selected.map((t) => (
                    <Chip key={t.id} tone="amber">trend: <b className="ml-1">{t.name}</b></Chip>
                  ))}
                </div>
              </Section>

              <Section icon={Crosshair} title="Focus — one focal point">
                <p className="text-sm text-gray-900 font-medium mb-1">
                  Primary: <span className="text-purple-700">{direction.focus.primary}</span>
                </p>
                <p className="text-xs text-gray-500 italic">“{direction.focus.rationale}”</p>
              </Section>

              <Section icon={ListOrdered} title="Visual Hierarchy">
                <ol className="space-y-1.5">
                  {direction.visualHierarchy.ranks.map((r) => (
                    <li key={r.role} className="flex items-center gap-2 text-sm">
                      <span className="w-5 h-5 rounded-full bg-purple-100 text-purple-700 text-[11px] font-bold flex items-center justify-center flex-shrink-0">
                        {r.priority}
                      </span>
                      <span className="text-gray-700 w-36">{r.role}</span>
                      <div className="flex-1 h-1.5 rounded-full bg-gray-100 overflow-hidden">
                        <div className="h-full bg-purple-400 rounded-full" style={{ width: `${r.emphasis * 100}%` }} />
                      </div>
                    </li>
                  ))}
                </ol>
              </Section>

              <Section icon={Waves} title="Movement & Balance">
                <p className="text-sm text-gray-700 mb-2">
                  <b>{direction.movement.name}</b> flow — eye path:{' '}
                  {direction.movement.path.join(' → ')} · ends at <b>{direction.movement.endpoint}</b>
                </p>
                <div className="flex flex-wrap gap-1.5">
                  <Chip tone="gray">{direction.balance.symmetry}</Chip>
                  <Chip tone="gray">{direction.balance.weightDistribution}</Chip>
                  <Chip tone="gray">rhythm: {direction.balance.rhythm}</Chip>
                  <Chip tone="gray">contrast: {direction.balance.contrastLevel}</Chip>
                  <Chip tone="gray">empty space ≥ {Math.round(direction.negativeSpace.targetEmptyRatio * 100)}%</Chip>
                </div>
              </Section>

              <Section icon={BookOpen} title="Storytelling">
                <p className="text-sm text-gray-700 mb-1">
                  {Object.entries(direction.storytelling.arc).map(([beat, role]) => `${beat}: ${role}`).join(' → ')}
                </p>
                <p className="text-xs text-gray-500">
                  Tone: {direction.storytelling.tone.join(', ')} — {direction.storytelling.voiceNote}
                </p>
              </Section>
            </div>

            <div className="space-y-6">
              <Section icon={Eye} title="Composition Intent">
                <ul className="space-y-1">
                  {direction.composition.notes.map((note, i) => (
                    <li key={i} className="text-sm text-gray-700">• {note}</li>
                  ))}
                </ul>
              </Section>

              <Section icon={LayoutTemplate} title="Layout Reasoning">
                <p className="text-sm font-medium text-gray-900 mb-2">
                  Recommended: <span className="text-purple-700">{direction.layoutReasoning.recommendedLayout}</span>
                </p>
                <ul className="space-y-1">
                  {direction.layoutReasoning.reasoning.map((r, i) => (
                    <li key={i} className="text-xs text-gray-500">— {r}</li>
                  ))}
                </ul>
              </Section>

              {plan && (
                <Section icon={Factory} title="Factory Plan (adapter output)">
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    <Chip>category: <b className="ml-1">{plan.category}</b></Chip>
                    {Object.entries(plan.overrides).map(([gene, value]) => (
                      <Chip key={gene} tone="gray">{gene}: <b className="ml-1">{`${value}`}</b></Chip>
                    ))}
                  </div>
                  {plan.futureEffects.length > 0 && (
                    <p className="text-xs text-amber-600 mb-3">
                      {plan.futureEffects.length} decision(s) reserved for future Design Engine support:{' '}
                      {plan.futureEffects.map((e) => e.effect).join(', ')}
                    </p>
                  )}
                  <div className="flex items-center gap-2">
                    <Button size="sm" onClick={runFactory} className="bg-purple-600 hover:bg-purple-700">
                      <Factory className="w-4 h-4 mr-1" /> Manufacture 3 with this direction
                    </Button>
                    <Button variant="outline" size="sm" onClick={copyDirection}>
                      {copied ? <CheckCircle2 className="w-4 h-4 mr-1 text-emerald-600" /> : <Copy className="w-4 h-4 mr-1" />}
                      {copied ? 'Copied' : 'Copy direction JSON'}
                    </Button>
                  </div>
                  {batch && (
                    <div className="mt-3 pt-3 border-t border-gray-100 text-sm">
                      {batch.complete ? (
                        <p className="text-emerald-600 flex items-center gap-1">
                          <CheckCircle2 className="w-4 h-4" />
                          {batch.templates.length} templates manufactured — scores:{' '}
                          {batch.templates.map((t) => t.qualityScore).join(', ')}
                          <span className="text-gray-400 ml-1">({batch.stats.attempts} attempts)</span>
                        </p>
                      ) : (
                        <p className="text-amber-600">
                          Batch incomplete after {batch.stats.attempts} attempts
                          ({batch.stats.rejectedQuality} quality / {batch.stats.rejectedDuplicate} duplicate rejects).
                        </p>
                      )}
                    </div>
                  )}
                </Section>
              )}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminAICreativeDirector;
